import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Camera, X, Loader2 } from "lucide-react";

const QrScanner = ({ onScan, onError }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const html5QrcodeRef = useRef(null);
  const containerRef = useRef(null);

  const startScanner = async () => {
    setIsLoading(true);
    setError(null);
    setIsScanning(true); // Prima mostra il container
  };

  // Questo effect si attiva quando isScanning diventa true
  useEffect(() => {
    if (!isScanning) return;

    const initializeScanner = async () => {
      try {
        // Aspetta che il DOM si aggiorni
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const element = document.getElementById("qr-reader-element");
        if (!element) {
          throw new Error("Container non trovato");
        }

        // Crea istanza scanner
        html5QrcodeRef.current = new Html5Qrcode("qr-reader-element");
        
        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        };
        
        await html5QrcodeRef.current.start(
          { facingMode: "environment" },
          config,
          (decodedText) => {
            if (onScan) onScan(decodedText);
            stopScanner();
          },
          (errorMessage) => {
            // Ignora errori di scansione continui
          }
        );
      } catch (err) {
        console.error("Scanner error:", err);
        setError(err.message || "Errore nell'avvio della fotocamera. Verifica i permessi.");
        if (onError) onError(err);
        setIsScanning(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeScanner();
  }, [isScanning, onScan, onError]);

  const stopScanner = async () => {
    if (html5QrcodeRef.current) {
      try {
        if (html5QrcodeRef.current.isScanning) {
          await html5QrcodeRef.current.stop();
        }
        html5QrcodeRef.current.clear();
      } catch (err) {
        console.error("Stop scanner error:", err);
      }
      html5QrcodeRef.current = null;
    }
    setIsScanning(false);
  };

  useEffect(() => {
    return () => {
      if (html5QrcodeRef.current) {
        try {
          if (html5QrcodeRef.current.isScanning) {
            html5QrcodeRef.current.stop();
          }
          html5QrcodeRef.current.clear();
        } catch (e) {}
      }
    };
  }, []);

  return (
    <div className="space-y-4">
      {!isScanning ? (
        <div className="text-center py-8">
          <Camera className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            Premi il pulsante per avviare la scansione
          </p>
          {error && (
            <p className="text-red-500 text-sm mb-4">{error}</p>
          )}
          <Button 
            onClick={startScanner} 
            disabled={isLoading}
            size="lg"
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoading ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Avvio...</>
            ) : (
              <><Camera className="mr-2 h-5 w-5" />Avvia Scansione</>
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isLoading ? "Inizializzazione..." : "Scansione in corso..."}
            </h3>
            <Button variant="ghost" size="icon" onClick={stopScanner}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}
          <div 
            id="qr-reader-element" 
            ref={containerRef}
            className="w-full max-w-md mx-auto rounded-lg overflow-hidden bg-black"
            style={{ minHeight: "300px" }}
          />
        </div>
      )}
    </div>
  );
};

export default QrScanner;
